/* eslint-disable indent */
import { Box, Typography, useTheme } from "@mui/material";
import React from "react";
import {
  EPrescriptionStatus,
  IMappedPrescription,
} from "../../../../../../services/prescriptions/prescriptions.interface";
import PrescriptionStatus from "../PrescriptionStatus/PrescriptionStatus";
import { CircleAlertIcon } from "../../../../../../components/Icons";
import DetailItem from "./DetailItem/DetailItem";
import AlertMessage from "./AlertMessage/AlertMessage";
import PrescriptionActionButtons from "./PrescriptionActionButtons/PrescriptionActionButtons";

interface IPrescriptionCardProps {
  prescription: IMappedPrescription;
  shopUrl: string;
}

const PrescriptionCard = ({ prescription, shopUrl }: IPrescriptionCardProps) => {
  const theme = useTheme();
  const { name, status, petName, prescribedAt, expirationDate, prescribedBy } = prescription;

  const getBorderColor = (status: EPrescriptionStatus) => {
    switch (status) {
      case EPrescriptionStatus.EXPIRED:
        return theme.palette.error.main;
      case EPrescriptionStatus.EXPIRING_SOON:
        return theme.palette.primary.main;
      default:
        return theme.palette.neutral.light;
    }
  };

  const borderColor = getBorderColor(status);

  return (
    <Box
      className="bg-white rounded-lg border flex flex-col gap-6"
      sx={{
        borderColor,
        padding: { xs: "16px", md: "24px" },
      }}
    >
      <Box className="flex items-center justify-between">
        <Typography fontSize={16}>{name}</Typography>
        <Box className="flex items-center gap-3">
          <PrescriptionStatus status={status} />
        </Box>
      </Box>

      <Box
        className="grid gap-6"
        sx={{
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        }}
      >
        <DetailItem label="Pet Name" value={petName} />
        <DetailItem label="Prescribed At" value={prescribedAt} />
        <DetailItem label="Expiration Date" value={expirationDate} />
        <DetailItem label="Prescribed By" value={prescribedBy} />
      </Box>

      <Box
        className="flex items-center justify-between border-t pt-6"
        sx={{ borderColor: "neutral.light", flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: 0 } }}
      >
        <PrescriptionActionButtons
          status={status}
          prescriptionId={prescription.id}
          itemId={prescription.itemId}
          source="shop"
          shopUrl={shopUrl}
        />

        {status === EPrescriptionStatus.EXPIRED && (
          <AlertMessage
            // eslint-disable-next-line max-len
            message="This prescription has expired and can’t be filled. Request a new prescription to continue treatment."
            icon={<CircleAlertIcon width={12} height={12} color={theme.palette.red.main} />}
            backgroundColor={theme.palette.red.light}
          />
        )}
        {status === EPrescriptionStatus.EXPIRING_SOON && (
          <AlertMessage
            message="You’re nearing the end of available refills. Renew your prescription to continue treatment."
            icon={<CircleAlertIcon width={12} height={12} color={theme.palette.yellow.main} />}
            backgroundColor={theme.palette.yellow.light}
          />
        )}
      </Box>
    </Box>
  );
};

export default PrescriptionCard;
